import { loginModules } from "@r";
import { clearAuthToken, setAuthToken, getAuthToken } from "@/utils/auth";

// 冷启动第一帧就要用的东西全部持久化到 localStorage:token、profile、菜单树、perms、roles。
// 这样刷新页面时 Vue 挂载前就已经有 perms,不会先渲一遍"没系统管理"再补回来。
// 后台 loadMenus 拉到新数据后覆盖,保持和后端一致。
const LS_KEY = "ds_gateway_user_state";

const readCache = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) || {};
  } catch (e) {
    return {};
  }
};

const writeCache = (state) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({
      profile: state.profile,
      menuTree: state.menuTree,
      perms: state.perms,
      roles: state.roles,
    }));
  } catch (e) {
    /* quota full / storage disabled — non-fatal */
  }
};

const clearCache = () => {
  try { localStorage.removeItem(LS_KEY); } catch (e) { /* noop */ }
};

const collectPerms = (menuTree) => {
  const perms = new Set();
  const walk = (nodes) => {
    if (!Array.isArray(nodes)) return;
    for (const n of nodes) {
      if (n && n.perms) perms.add(n.perms);
      if (n && n.children && n.children.length) walk(n.children);
    }
  };
  walk(menuTree);
  return Array.from(perms);
};

const cached = readCache();

const state = {
  token: getAuthToken(),
  profile: cached.profile || {},
  menuTree: cached.menuTree || [],
  perms: cached.perms || [],
  roles: cached.roles || [],
  // 是否已经完成过一次和服务端的同步。冷启动时如果 token 存在但还没同步,
  // main.js 的 bootstrap 会先跑一次 loadMenus 再挂载 Vue。
  menusLoaded: false,
  routesMounted: false,
};

const getters = {
  hasPerm: (state) => (code) => {
    if (!code) return true;
    // admin 不再豁免。权限完全走 sys_role_menu → perms 数组。
    return state.perms.indexOf(code) !== -1;
  },
  hasRole: (state) => (role) => state.roles.indexOf(role) !== -1,
};

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token || "";
  },
  SET_MENUS(state, tree) {
    state.menuTree = Array.isArray(tree) ? tree : [];
    state.perms = collectPerms(state.menuTree);
    state.menusLoaded = true;
    writeCache(state);
  },
  SET_ROLES(state, roles) {
    state.roles = Array.isArray(roles) ? roles : [];
    writeCache(state);
  },
  SET_PROFILE(state, profile) {
    state.profile = profile || {};
    writeCache(state);
  },
  SET_ROUTES_MOUNTED(state, v) {
    state.routesMounted = !!v;
  },
  RESET(state) {
    state.token = "";
    state.profile = {};
    state.menuTree = [];
    state.perms = [];
    state.roles = [];
    state.menusLoaded = false;
    state.routesMounted = false;
    clearCache();
  },
};

const actions = {
  async login({ commit, dispatch }, { username, password }) {
    const res = await loginModules.login({ username, password });
    const token = res && res.data;
    if (!token) throw new Error((res && res.message) || "登录失败");
    setAuthToken(token);
    // 清空上一个用户遗留缓存,再写新 token
    commit("RESET");
    commit("SET_TOKEN", token);
    commit("SET_PROFILE", { username });
    await dispatch("loadMenus");
    return token;
  },
  async loadMenus({ commit, state }) {
    const [menuRes, meRes] = await Promise.all([
      loginModules.getMenuRouters(),
      loginModules.me().catch(() => ({ data: {} })),
    ]);
    commit("SET_MENUS", (menuRes && menuRes.data) || []);
    const me = (meRes && meRes.data) || {};
    commit("SET_ROLES", me.roles || []);
    if (me.user) commit("SET_PROFILE", me.user);
    return state.menuTree;
  },
  async logout({ commit }) {
    try {
      await loginModules.logout();
    } finally {
      clearAuthToken();
      commit("RESET");
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
